import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { apiRequest } from "../../shared/api/client";
import {
  WEEKLY_PLANNING_API_BASE,
  activateWeeklyPlanRequestSchema,
  createWeeklyPlanRequestSchema,
  saveWeeklyPlanRequestSchema,
  weeklyPlanActivationResponseSchema,
  weeklyPlanActivationPreviewSchema,
  weeklyPlanCarryoverResponseSchema,
  weeklyPlanDetailResponseSchema,
  weeklyPlanImportResponseSchema,
  weeklyPlanListResponseSchema,
  weeklyPlanningReferenceDataResponseSchema,
  verifyWeeklyPlanWorkOrderRequestSchema,
  type WeeklyPlan,
  type WeeklyPlanActivationPreview,
  type WeeklyPlanCarryoverCandidate,
  type WeeklyPlanRow,
} from "../../../shared/weekly-planning";

function mondayInIstanbul() {
  const today = new Date();
  const value = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(today);
  const date = new Date(`${value}T12:00:00Z`);
  const weekday = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() - weekday + 1);
  return date.toISOString().slice(0, 10);
}

function message(error: unknown) {
  if (error instanceof z.ZodError) {
    const issue = error.issues[0];
    const field = String(issue?.path.at(-1) ?? "");
    const labels: Record<string, string> = { lineId: "Her plan satırında bir hat seçin.", productCode: "Üretim satırlarında ürün kodu girin.", productName: "Ürün veya etkinlik adını girin.", quantity: "Üretim miktarını geçerli bir değer olarak girin.", unit: "Üretim birimini seçin.", capacityPerHour: "Saatlik kapasiteyi girin.", durationHours: "Etkinlik süresini girin." };
    return labels[field] ?? issue?.message ?? "Plan verisi doğrulanamadı. Sayfayı yenileyip tekrar deneyin.";
  }
  return error instanceof Error ? error.message : "Plan güncellenemedi.";
}

function emptyRow(kind: WeeklyPlanRow["kind"], lineId: string, sequence: number, weekStart: string, dayStartsAt: "00:00" | "08:00"): WeeklyPlanRow {
  void weekStart; void dayStartsAt;
  const missingFields = kind === "PRODUCTION"
    ? ["productCode", "quantity", "unit", "capacityPerHour"] as const
    : ["durationHours"] as const;
  return {
    id: crypto.randomUUID(), executionKey: crypto.randomUUID(), kind, lineId, lineName: "Hat seçilmedi", sequence,
    workOrderNo: null, productCode: null, productName: kind === "PRODUCTION" ? "" : "Planlı etkinlik", quantity: null,
    unit: null, capacityPerHour: null, durationHours: null, state:"INCOMPLETE",
    missingFields:lineId ? [...missingFields] : ["lineId",...missingFields], plannedHours: null,
    scheduledHours: null, scheduledQuantity: null, deferredQuantity: null, scheduledStartAt: null, scheduledEndAt: null,
    note: null, carryoverFromRowId: null, source:null,
  };
}

function rowsForSave(rows: WeeklyPlanRow[]) {
  return rows.map((row) => ({
    id: row.id, kind: row.kind, lineId: row.lineId, sequence: row.sequence, workOrderNo: row.workOrderNo,
    productCode: row.productCode, productName: row.productName, quantity: row.quantity, unit: row.unit,
    capacityPerHour: row.capacityPerHour, durationHours: row.durationHours, note: row.note,
    carryoverFromRowId: row.carryoverFromRowId,
  }));
}

function renumber(rows: WeeklyPlanRow[]) {
  return rows.map((row, index) => ({ ...row, sequence: index + 1 }));
}

export function useWeeklyPlanningWorkspace(canEdit: boolean, dataRefreshVersion: number, canActivate: boolean, initialWeekStart?: string | null) {
  const [weekStart, setWeekStart] = useState(() => initialWeekStart ?? mondayInIstanbul());
  const [plan, setPlan] = useState<WeeklyPlan | null>(null);
  const [lines, setLines] = useState<Array<{ id: string; code: string; name: string; active: boolean }>>([]);
  const [carryovers, setCarryovers] = useState<WeeklyPlanCarryoverCandidate[]>([]);
  const [selectedCarryoverIds, setSelectedCarryoverIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [verifyingRowId, setVerifyingRowId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [activationConfirmationOpen, setActivationConfirmationOpen] = useState(false);
  const [activationPreview, setActivationPreview] = useState<WeeklyPlanActivationPreview | null>(null);
  const [excludedRowsAcknowledged, setExcludedRowsAcknowledged] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const loadRunRef = useRef(0);
  const activationKeyRef = useRef({ key: "", planRevision: "" });

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const load = useCallback(async (signal?: AbortSignal, force = false) => {
    if (pendingRef.current || (dirty && !force)) { setLoading(false); return; }
    const run = ++loadRunRef.current;
    setLoading(true); setError(null);
    try {
      const [referencesRaw, plansRaw, carryoversRaw] = await Promise.all([
        apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/reference-data`, { signal }),
        apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans?${new URLSearchParams({ weekStart, history: "1" })}`, { signal }),
        apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/carryovers?${new URLSearchParams({ weekStart })}`, { signal }),
      ]);
      if (signal?.aborted || run !== loadRunRef.current) return;
      setLines(weeklyPlanningReferenceDataResponseSchema.parse(referencesRaw).lines);
      const summaries = weeklyPlanListResponseSchema.parse(plansRaw).items;
      const latest = summaries.find((item) => item.status === "DRAFT") ?? summaries.find((item) => item.status === "ACTIVE") ?? null;
      const nextCarryovers = weeklyPlanCarryoverResponseSchema.parse(carryoversRaw).items;
      setCarryovers(nextCarryovers);
      if (!latest) { setPlan(null); setSelectedCarryoverIds([]); setDirty(false); return; }
      const detailRaw = await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans/${latest.id}`, { signal });
      if (!signal?.aborted && run === loadRunRef.current) { setPlan(weeklyPlanDetailResponseSchema.parse(detailRaw).plan); setDirty(false); }
    } catch (failure) {
      if (!signal?.aborted && run === loadRunRef.current) setError(message(failure));
    } finally { if (!signal?.aborted && run === loadRunRef.current) setLoading(false); }
  }, [dirty, weekStart]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); ++loadRunRef.current; };
  }, [dataRefreshVersion, load]);

  const updatePlan = useCallback((transform: (current: WeeklyPlan) => WeeklyPlan) => {
    if (!canEdit || pendingRef.current) return;
    setPlan((current) => current && current.status === "DRAFT" ? transform(current) : current);
    setDirty(true);
  }, [canEdit]);

  const create = useCallback(async () => {
    if (!canEdit || pendingRef.current) return;
    pendingRef.current = true;
    ++loadRunRef.current;
    setSaving(true); setError(null);
    try {
      const body = createWeeklyPlanRequestSchema.parse({
        weekStart,
        dayStartsAt: plan?.dayStartsAt ?? "08:00",
        executionMode: plan?.executionMode ?? "CAPACITY_SCHEDULED",
        rows: [],
        basePlanId: plan?.status === "ACTIVE" ? plan.id : undefined,
      });
      const raw = await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans`, { method: "POST", body: JSON.stringify(body) });
      setPlan(weeklyPlanDetailResponseSchema.parse(raw).plan); setSelectedCarryoverIds([]);
      setDirty(false);
    } catch (failure) { setError(message(failure)); }
    finally { pendingRef.current = false; setSaving(false); }
  }, [canEdit, plan, saving, weekStart]);

  const save = useCallback(async () => {
    if (!plan || !canEdit || !dirty || pendingRef.current) return;
    pendingRef.current = true;
    ++loadRunRef.current;
    setSaving(true); setError(null);
    try {
      const body = saveWeeklyPlanRequestSchema.parse({ expectedRevision: plan.revision, title: plan.title, note: plan.note, dayStartsAt: plan.dayStartsAt, executionMode: plan.executionMode, rows: rowsForSave(plan.rows), carryoverRowIds: selectedCarryoverIds });
      const raw = await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans/${plan.id}`, { method: "PUT", body: JSON.stringify(body) });
      setPlan(weeklyPlanDetailResponseSchema.parse(raw).plan); setSelectedCarryoverIds([]);
      setDirty(false);
      try {
        const carryoversRaw = await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/carryovers?${new URLSearchParams({ weekStart: plan.weekStart })}`);
        setCarryovers(weeklyPlanCarryoverResponseSchema.parse(carryoversRaw).items);
      } catch (failure) {
        setError(`Taslak kaydedildi; devir listesi yenilenemedi. ${message(failure)}`);
      }
    } catch (failure) { setError(message(failure)); }
    finally { pendingRef.current = false; setSaving(false); }
  }, [canEdit, dirty, plan, saving, selectedCarryoverIds]);

  const activate = useCallback(async () => {
    if (!plan || !canActivate || dirty || pendingRef.current || !activationPreview
      || activationPreview.revision !== plan.revision
      || (activationPreview.excludedRows.length > 0 && !excludedRowsAcknowledged)) return;
    pendingRef.current = true;
    ++loadRunRef.current;
    setActivating(true); setError(null);
    try {
      const planRevision = `${plan.id}:${plan.revision}`;
      if (activationKeyRef.current.planRevision !== planRevision) activationKeyRef.current = { planRevision, key: crypto.randomUUID() };
      const body = activateWeeklyPlanRequestSchema.parse({
        expectedRevision: plan.revision, idempotencyKey: activationKeyRef.current.key,
        comparisonDigest: activationPreview.digest,
        acknowledgedExcludedRowIds: activationPreview.excludedRows.map((row) => row.rowId),
      });
      const raw = await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans/${plan.id}/activate`, { method: "POST", body: JSON.stringify(body) });
      setPlan(weeklyPlanActivationResponseSchema.parse(raw).plan);
      setActivationConfirmationOpen(false); setActivationPreview(null); setExcludedRowsAcknowledged(false);
      activationKeyRef.current = { key: "", planRevision: "" };
    } catch (failure) { setError(message(failure)); }
    finally { pendingRef.current = false; setActivating(false); }
  }, [activationPreview, canActivate, dirty, excludedRowsAcknowledged, plan]);

  const importFile = useCallback(async (file: File) => {
    if (!canEdit || pendingRef.current) return;
    if (dirty && !window.confirm("Kaydedilmemiş taslak değişiklikleri içe aktarılan Excel taslağıyla değiştirilecek. Devam edilsin mi?")) return;
    pendingRef.current = true; ++loadRunRef.current; setImporting(true); setError(null); setNotice(null);
    try {
      const form = new FormData(); form.append("file",file); form.append("dayStartsAt",plan?.dayStartsAt ?? "00:00");
      form.append("executionMode", plan?.executionMode ?? "CAPACITY_SCHEDULED");
      if (plan?.status === "DRAFT") {
        form.append("expectedPlanId",plan.id); form.append("expectedRevision",String(plan.revision));
      }
      const raw = await apiRequest<unknown>("/api/integrations/production/mikro/production-plan/weekly-draft", { method:"POST",body:form });
      const imported = weeklyPlanImportResponseSchema.parse(raw);
      setPlan(imported.plan); setWeekStart(imported.plan.weekStart); setDirty(false); setSelectedCarryoverIds([]);
      setActivationPreview(null); setActivationConfirmationOpen(false); setExcludedRowsAcknowledged(false);
      const unavailable = imported.importSummary.readOnlyUnitLookup === "UNAVAILABLE" ? " Mikro birim doğrulaması kullanılamayan satırlar eksik bırakıldı." : "";
      setNotice(`${imported.importSummary.readyRowCount} satır hazır; ${imported.importSummary.incompleteRowCount} satır eksik bilgi bekliyor; ${imported.importSummary.dependencyBlockedRowCount} satır önceki eksik işe bağlı.${unavailable}`);
    } catch (failure) { setError(message(failure)); }
    finally { pendingRef.current=false; setImporting(false); }
  }, [canEdit,dirty,plan]);

  const openActivation = useCallback(async () => {
    if (!plan || !canActivate || dirty || pendingRef.current) return;
    const run=++loadRunRef.current;
    pendingRef.current=true; setActivating(true); setError(null);
    try {
      const raw=await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans/${plan.id}/activation-preview`);
      const comparison=weeklyPlanActivationPreviewSchema.parse(raw);
      if(run!==loadRunRef.current || comparison.planId!==plan.id || comparison.revision!==plan.revision)return;
      setActivationPreview(comparison); setExcludedRowsAcknowledged(false);
      activationKeyRef.current={key:crypto.randomUUID(),planRevision:`${plan.id}:${plan.revision}`};
      setActivationConfirmationOpen(true);
    } catch(failure){setError(message(failure));}
    finally{pendingRef.current=false;setActivating(false);}
  },[canActivate,dirty,plan]);

  const verifyWorkOrder = useCallback(async (rowId: string, identity: {
    salesOrderNo: string | null;
    customerCode: string | null;
    correctionReason: string | null;
  }) => {
    if (!plan || !canEdit || dirty || pendingRef.current || plan.status !== "DRAFT") return;
    const row = plan.rows.find((candidate) => candidate.id === rowId);
    if (!row?.workOrderNo || plan.executionMode !== "OPERATOR_SELECTED") return;
    pendingRef.current = true;
    ++loadRunRef.current;
    setVerifyingRowId(rowId);
    setError(null);
    setNotice(null);
    try {
      const body = verifyWeeklyPlanWorkOrderRequestSchema.parse({
        expectedRevision: plan.revision,
        workOrderNo: row.workOrderNo,
        ...identity,
      });
      const raw = await apiRequest<unknown>(
        `/api/integrations/production/mikro/production-plan/weekly-draft/${plan.id}/rows/${rowId}/verify-work-order`,
        { method: "POST", body: JSON.stringify(body) },
      );
      const verified = weeklyPlanDetailResponseSchema.parse(raw).plan;
      setPlan(verified);
      setDirty(false);
      setActivationPreview(null);
      setActivationConfirmationOpen(false);
      setNotice(`${row.workOrderNo} iş emri Mikro'da ürün, sipariş ve müşteri kimliğiyle doğrulandı.`);
    } catch (failure) {
      setError(message(failure));
    } finally {
      pendingRef.current = false;
      setVerifyingRowId(null);
    }
  }, [canEdit, dirty, plan]);

  const discard = useCallback(async () => {
    if (!plan || plan.status !== "DRAFT" || !canEdit || pendingRef.current) return;
    if (!window.confirm("Bu taslak ve kaydedilmemiş düzenlemeler bırakılacak. Taslak geçmişte korunur; etkin üretim planı değişmez. Devam edilsin mi?")) return;
    pendingRef.current = true;
    ++loadRunRef.current;
    setSaving(true); setError(null);
    let discarded = false;
    try {
      const raw = await apiRequest<unknown>(`${WEEKLY_PLANNING_API_BASE}/plans/${plan.id}/discard`, {
        method: "POST", body: JSON.stringify({ expectedRevision: plan.revision }),
      });
      weeklyPlanDetailResponseSchema.parse(raw);
      setPlan(null); setDirty(false); setSelectedCarryoverIds([]); setActivationConfirmationOpen(false);
      activationKeyRef.current = { key: "", planRevision: "" };
      discarded = true;
    } catch (failure) { setError(message(failure)); }
    finally { pendingRef.current = false; setSaving(false); }
    if (discarded) await load(undefined, true);
  }, [canEdit, load, plan]);

  const actions = useMemo(() => ({
    setWeekStart: (value: string) => { if (!value || pendingRef.current) return; if (!dirty || window.confirm("Kaydedilmemiş taslak değişiklikleri var. Haftayı değiştirmek istiyor musunuz?")) { ++loadRunRef.current; setPlan(null); setCarryovers([]); setSelectedCarryoverIds([]); setDirty(false); setWeekStart(value); setActivationConfirmationOpen(false); activationKeyRef.current = { key: "", planRevision: "" }; } },
    create,
    title: (value: string) => updatePlan((current) => ({ ...current, title: value })),
    note: (value: string) => updatePlan((current) => ({ ...current, note: value || null })),
    dayStartsAt: (value: "00:00" | "08:00") => updatePlan((current) => ({ ...current, dayStartsAt: value })),
    executionMode: (value: WeeklyPlan["executionMode"]) => updatePlan((current) => ({ ...current, executionMode: value })),
    addRow: (kind: WeeklyPlanRow["kind"]) => updatePlan((current) => ({ ...current, rows: [...current.rows, emptyRow(kind, current.rows[0]?.lineId ?? "", current.rows.length + 1, current.weekStart, current.dayStartsAt)] })),
    updateRow: (rowId: string, field: string, value: string) => updatePlan((current) => ({ ...current, rows: current.rows.map((row) => {
      if (row.id !== rowId) return row;
      if (field === "kind") return value === "ACTIVITY" ? { ...row, kind: "ACTIVITY", productCode: null, workOrderNo: null, quantity: null, unit: null, capacityPerHour: null, durationHours: row.durationHours ?? row.source?.suggestedDurationHours ?? null } : { ...row, kind: "PRODUCTION", durationHours: null };
      if (field === "quantity" || field === "capacityPerHour" || field === "durationHours") return { ...row, [field]: value === "" ? null : Number(value) };
      if (field === "lineId") return { ...row, lineId: value || null, lineName: lines.find((line) => line.id === value)?.name ?? row.source?.sourceLineName ?? "Hat seçilmedi" };
      if (field === "unit") return { ...row, unit: value === "" ? null : value as WeeklyPlanRow["unit"] };
      if (field === "productCode" || field === "workOrderNo") return { ...row, [field]: value || null };
      return { ...row, [field]: value };
    }) })),
    removeRow: (rowId: string) => updatePlan((current) => ({ ...current, rows: renumber(current.rows.filter((row) => row.id !== rowId)) })),
    moveRow: (rowId: string, direction: -1 | 1) => updatePlan((current) => {
      const index = current.rows.findIndex((row) => row.id === rowId); const row = current.rows[index];
      if (!row) return current;
      const lineKey=(candidate:WeeklyPlanRow)=>candidate.lineId ?? candidate.source?.sourceLineName ?? "";
      const sameLineIndexes = current.rows.map((candidate, candidateIndex) => lineKey(candidate) === lineKey(row) ? candidateIndex : -1).filter((candidateIndex) => candidateIndex >= 0);
      const lineIndex = sameLineIndexes.indexOf(index); const target = sameLineIndexes[lineIndex + direction];
      if (target == null) return current;
      const rows = [...current.rows]; [rows[index], rows[target]] = [rows[target], rows[index]];
      return { ...current, rows: renumber(rows) };
    }),
    toggleCarryover: (candidate: WeeklyPlanCarryoverCandidate, selected: boolean) => {
      if (!canEdit || pendingRef.current) return;
      if (selected && (!candidate.canCarryOver || !candidate.unit || !candidate.capacityPerHour)) return;
      setPlan((current) => !current ? current : !selected && current.rows.some((row) => row.carryoverFromRowId === candidate.sourceRowId)
        ? { ...current, rows: renumber(current.rows.filter((row) => row.carryoverFromRowId !== candidate.sourceRowId)) }
        : current);
      setSelectedCarryoverIds((currentIds) => selected
        ? currentIds.includes(candidate.sourceRowId) ? currentIds : [...currentIds, candidate.sourceRowId]
        : currentIds.filter((id) => id !== candidate.sourceRowId));
      setDirty(true);
    },
    importFile, save, discard, activate, openActivation, verifyWorkOrder,
    setExcludedRowsAcknowledged,
    cancelActivation: () => { activationKeyRef.current = { key: "", planRevision: "" }; setActivationConfirmationOpen(false); setActivationPreview(null); setExcludedRowsAcknowledged(false); },
    retry: () => { if (pendingRef.current) return; if (!dirty || window.confirm("Kaydedilmemiş taslak değişikliklerini bırakıp sunucudan yeniden yüklemek istiyor musunuz?")) { setDirty(false); void load(undefined, true); } },
  }), [activate, canEdit, create, dirty, discard, importFile, lines, load, openActivation, save, updatePlan, verifyWorkOrder]);

  return { plan, lines, carryovers, selectedCarryoverIds, weekStart, loading, saving, activating, importing, error, notice, dirty,
    verifyingRowId, activationConfirmationOpen, activationPreview, excludedRowsAcknowledged, ...actions };
}
